import React, { useState } from "react";
import { Share2, Check } from "lucide-react";
import { toast } from "@/lib/toast";

// The founding-number share — our one organic growth mechanic. The number lives in the SHARE TEXT;
// the link points at the public homepage (opticswap.com), which strangers can open (a profile link
// is behind the beta gate and would dead-end). Native share sheet on mobile, copy-to-clipboard on
// desktop. The homepage already carries a rich OG card (public/index.html og-card-v1.jpg), so the
// shared link previews well today; per-listing dynamic OG is a separate concern for listing shares.
const GOLD = "#E8C46A";
const GOLD_MID = "#7A6428";
const GOLD_LABEL = "#C7A75A";
const SITE_URL = "https://opticswap.com";
const pad3 = (n) => String(n).padStart(3, "0");

export async function shareFounding(seat, onCopied) {
  const n = Number(seat);
  if (!Number.isInteger(n) || n < 1) return;
  const num = pad3(n);
  const url = `${SITE_URL}/?ref=founding`;
  const text = `I'm founding member #${num} on OpticSwap — the protected-payment marketplace for rifle scopes & optics. The money doesn't move until the gear arrives.`;
  if (typeof navigator !== "undefined" && navigator.share) {
    try { await navigator.share({ title: `OpticSwap · Founding Member #${num}`, text, url }); }
    catch { /* user dismissed the share sheet */ }
    return;
  }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
    onCopied?.();
    toast.success("Copied — paste your founding link anywhere.");
  } catch {
    toast.error("Couldn't copy automatically — long-press the link to share it.");
  }
}

export function FoundingShareButton({ seat, className = "", testid = "founding-share-btn" }) {
  const [copied, setCopied] = useState(false);
  const n = Number(seat);
  if (!Number.isInteger(n) || n < 1) return null;
  const go = () => shareFounding(n, () => { setCopied(true); setTimeout(() => setCopied(false), 2200); });
  return (
    <button
      data-testid={testid}
      onClick={go}
      className={`inline-flex items-center justify-center gap-2 font-heading font-bold uppercase text-sm px-4 py-2.5 transition-all hover:brightness-110 ${className}`}
      style={{ backgroundColor: GOLD, color: "#1A150B" }}
    >
      {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Share2 className="w-4 h-4" /> Share</>}
    </button>
  );
}

// The gold "YOU'RE FOUNDING MEMBER #NN" card with a Share button beside it — sized so a phone
// screenshot reads well. Used on the claim confirmation and on the member's own profile.
export default function FoundingShareCard({ seat, className = "", testid = "founding-share-card" }) {
  const n = Number(seat);
  if (!Number.isInteger(n) || n < 1) return null;
  const num = pad3(n);
  return (
    <div
      data-testid={testid}
      className={`flex items-center justify-between gap-4 ${className}`}
      style={{ border: `1px solid ${GOLD_MID}`, background: "linear-gradient(180deg, #2A2213, #17120A)", padding: "16px 18px" }}
    >
      <div className="min-w-0 text-left">
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.16em", color: GOLD_LABEL }}>
          YOU'RE FOUNDING MEMBER
        </div>
        <div
          className="font-heading tabular-nums"
          data-testid={`${testid}-seat`}
          style={{ fontWeight: 800, fontSize: "clamp(40px, 12vw, 58px)", color: GOLD, lineHeight: 0.9, marginTop: 2 }}
        >
          #{num}
        </div>
      </div>
      <FoundingShareButton seat={n} className="shrink-0" />
    </div>
  );
}
